// Some cameras record codecs no browser can decode in a <video> element —
// the Brinno TLC300 writes Motion JPEG inside an .MP4 container (#473). For
// those sources the analyzevideo worker uploads a browser-safe H.264 proxy
// and points video.proxyPath at it. In-app playback and scrubbing prefer the
// proxy; rendering always uses the original at video.storagePath.

import { isAnalysisInFlight } from '../lib/editDraft';

// proxyStatus is only ever set for a source browsers can't decode. While it
// is 'pending' or 'failed' there is NO playable source — pointing a player at
// the original is exactly the black-preview symptom #473 is about.
const PREVIEW_BLOCKED_STATUSES = ['pending', 'failed'];

export function isPreviewBlocked(video) {
    return PREVIEW_BLOCKED_STATUSES.includes(video?.proxyStatus);
}

// Returning null while the preview is blocked is the single gate for every
// playback surface: ProjectView feeds this to `videoUrl`, and all four
// consumers (Footage card, Overview player, Edits scrubber, watermark poster
// frame) already render behind `{videoUrl && ...}`, so they hide together.
export function resolveSourceVideoPath(video) {
    if (isPreviewBlocked(video)) {
        return null;
    }
    return video?.proxyPath || video?.storagePath || null;
}

// #478 — "is a playable preview still on its way?", the question the loading
// placeholder answers. There are two ways to be waiting:
//
//   1. proxyStatus === 'pending' — the worker probed the codec and said so.
//   2. The pre-probe window. Before that probe lands proxyStatus is unset, so
//      the resolved path is the ORIGINAL and an undecodable source renders as
//      a black player. Nothing up front tells that apart from a healthy file —
//      the browser only reports it by failing to load — so the caller passes
//      the media error it observed in as `sourceErrored`.
//
// The error alone is not enough: once analysis is terminal no proxy is coming,
// so a failed player is simply broken and must stay visible rather than hide
// behind a bar that would spin forever. `isAnalysisPending` covers the sliver
// after upload-complete where the client has requested analysis but the doc
// still carries the previous status.
//
// Lives here, beside the proxyStatus gate, because it answers the same question
// for the same four playback surfaces — a tab holding its own copy would fix
// only that tab.
export function isSourcePreviewPending(video, { sourceErrored = false, isAnalysisPending = false } = {}) {
    if (video?.proxyStatus === 'pending') return true;
    return !!sourceErrored && (!!isAnalysisPending || isAnalysisInFlight(video));
}
